/**
 * UTM parameter utilities for tracking marketing campaigns
 */

import { UTMParameters } from '../types/analytics';

/**
 * Extracts all UTM parameters from the current URL
 * @returns Object containing all present UTM parameters
 */
export const extractUTMParameters = (): UTMParameters => {
  const params = new URLSearchParams(window.location.search);
  const utmParams: UTMParameters = {};

  // Extract each standard UTM parameter if present
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

  utmKeys.forEach(key => {
    const value = params.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });

  return utmParams;
};

/**
 * Converts UTM parameters object to URL query string
 * @param utmParams UTM parameters object
 * @returns URL-encoded query string (without leading '?')
 */
export const utmParamsToString = (utmParams: UTMParameters): string => {
  const params = new URLSearchParams();

  Object.entries(utmParams).forEach(([key, value]) => {
    if (value) {
      params.append(key, value);
    }
  });

  return params.toString();
};

/**
 * Formats UTM parameters for Google Play Store referrer parameter
 * Play Store expects UTM params in the 'referrer' query parameter
 * @param utmParams UTM parameters object
 * @returns Formatted referrer string for Play Store URL
 */
export const formatPlayStoreReferrer = (utmParams: UTMParameters): string => {
  if (Object.keys(utmParams).length === 0) {
    return '';
  }

  return utmParamsToString(utmParams);
};

/**
 * Appends UTM parameters to a Play Store URL via the referrer parameter
 * @param baseUrl Base Play Store URL
 * @param utmParams UTM parameters to append
 * @returns Complete URL with UTM parameters in referrer
 */
export const appendUTMToPlayStoreUrl = (baseUrl: string, utmParams: UTMParameters): string => {
  const referrer = formatPlayStoreReferrer(utmParams);

  if (!referrer) {
    return baseUrl;
  }

  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}referrer=${encodeURIComponent(referrer)}`;
};

/**
 * Checks if the current URL has any UTM parameters
 * @returns True if any UTM parameters are present
 */
export const hasUTMParameters = (): boolean => {
  const utmParams = extractUTMParameters();
  return Object.keys(utmParams).length > 0;
};
